import { ReactNode, createContext, useState, useCallback, useMemo, useContext } from "react";

import config from "../config";
import { messages } from "../langs";
import { TooltipId } from "../types";

interface ContextProps {
  locale: string;
  setLocale: (val: string) => void;
  hasFinishedLanding: boolean;
  setHasFinishedLanding: (val: boolean) => void;
  clearedTooltips: TooltipId[];
  checkIsClearedTooltips: (val: TooltipId) => boolean;
  setClearedTooltips: (val: TooltipId) => void;
}

interface ProviderProps {
  children: ReactNode;
}

const UserContext = createContext<Partial<ContextProps>>({});

const UserProvider = ({ children }: ProviderProps): JSX.Element => {
  const savedLocale = localStorage.getItem("locale");
  const [browserLocale] = navigator.language.split("-");
  const [locale, updateLocale] = useState(savedLocale || browserLocale);

  const setLocale = useCallback((val) => {
    updateLocale(val);
    localStorage.setItem("locale", val);
  }, []);

  const savedLandingStatus = localStorage.getItem("finishLanding") || "";
  const [hasFinishedLanding, updateHasFinishedLanding] = useState<boolean>(!!savedLandingStatus);
  const setHasFinishedLanding = useCallback((val: boolean) => {
    updateHasFinishedLanding(val);
    localStorage.setItem("finishLanding", val.toString());
  }, []);

  const savedClearedTooltips = localStorage.getItem("clearedTooltips")
    ? JSON.parse(localStorage.getItem("clearedTooltips") ?? "[]")
    : [];
  const [clearedTooltips, updateClearedTooltips] = useState<TooltipId[]>(savedClearedTooltips);

  const setClearedTooltips = useCallback(
    (val) => {
      updateClearedTooltips((prev) => [...prev, val]);
      localStorage.setItem("clearedTooltips", JSON.stringify([...clearedTooltips, val]));
    },
    [clearedTooltips]
  );

  const value = useMemo(
    () => ({
      locale: messages[locale] ? locale : config.DEFAULT_LOCALE,
      setLocale,
      hasFinishedLanding,
      setHasFinishedLanding,
      clearedTooltips,
      checkIsClearedTooltips: (val: TooltipId) => clearedTooltips.find((item) => item === val) !== undefined,
      setClearedTooltips,
    }),
    [locale, setLocale, hasFinishedLanding, setHasFinishedLanding, clearedTooltips, setClearedTooltips]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useUser = (): ContextProps => useContext(UserContext) as ContextProps;

export { UserProvider, useUser };
